import { UserInfo } from '../components/onboarding/steps/ScheduleVisit'
import api from './api'

export interface CreateMajorityShareholderCompleteDto {
  firstName: string
  paternalLastName: string
  maternalLastName: string
  rfc: string
  birthDate: string
  nationality: string
  phoneNumber: string
  email: string
  street: string
  exteriorNumber: string
  interiorNumber?: string
  betweenStreets: string
  postalCode: string
  neighborhood: string
  city?: string
  municipality?: string
  state: string
  stateAbbreviation?: string
  country?: string
  creditApplicationId?: string
}

export interface MajorityShareholder {
  id: string
  firstName: string
  paternalLastName: string
  maternalLastName: string
  rfc: string
  birthDate: string
  nationality: string
  phoneNumber: string
  email: string
  active: boolean
  verified: boolean
  addresses?: Array<{
    id: string
    type: string
    street: string
    exteriorNumber: string
    interiorNumber?: string
    neighborhood: string
    postalCode: string
    city?: string
    municipality?: string
    state: string
    stateAbbreviation?: string
    country: string
    reference?: string
    isPrimary: boolean
  }>
  documents?: Array<{
    id: string
    type: string
    url?: string
    fileName?: string
  }>
}

export interface IneDocument {
  url: string
  fileName: string
  mimeType: string
  side: 'frente' | 'reverso'
}

export interface MajorityShareholderForLegalRep {
  id: string
  firstName: string
  paternalLastName: string
  maternalLastName: string
  rfc: string
  phoneNumber: string
  email: string
  ineDocuments?: IneDocument[]
}

class MajorityShareholderService {
  async createComplete(
    formData: any,
    files: { ineFront: File | null; ineBack: File | null },
    creditApplicationId?: string | null
  ): Promise<MajorityShareholder> {
    const formDataToSend = new FormData()

    // Agregar datos del formulario
    const shareholderData = this.transformFormDataToDto(formData)

    // Log para depuración
    console.log('Form data received:', formData)
    console.log('Transformed DTO:', shareholderData)
    console.log('Neighborhood value:', shareholderData.neighborhood)

    // Agregar cada campo individualmente al FormData
    Object.keys(shareholderData).forEach(key => {
      const value =
        shareholderData[key as keyof CreateMajorityShareholderCompleteDto]
      if (value !== undefined && value !== null) {
        formDataToSend.append(key, value.toString())
      }
    })

    // Agregar archivos si existen
    if (files.ineFront) {
      formDataToSend.append('ineFrontImage', files.ineFront)
    }
    if (files.ineBack) {
      formDataToSend.append('ineBackImage', files.ineBack)
    }

    if (creditApplicationId) {
      formDataToSend.append('creditApplicationId', creditApplicationId)
    }

    const response = await api.post(
      '/majority-shareholder/complete',
      formDataToSend,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )

    console.log('Create shareholder response:', response.data)

    // Handle the response structure {success: true, data: {...}}
    if (response.data.success && response.data.data) {
      return response.data.data
    }

    // Fallback for direct object response
    return response.data
  }

  async getShareholders(): Promise<MajorityShareholder[]> {
    const response = await api.get('/majority-shareholder')
    console.log('Get shareholders response:', response.data)

    // Handle the response structure {success: true, data: [...]}
    if (response.data.success && response.data.data) {
      return response.data.data
    }

    // Fallback for direct array response
    return response.data || []
  }

  async getShareholderById(id: string): Promise<MajorityShareholder> {
    const response = await api.get(`/majority-shareholder/${id}`)

    if (response.data.success && response.data.data) {
      return response.data.data
    }

    return response.data
  }

  async getForLegalRepresentative(
    creditApplicationId?: string | null
  ): Promise<MajorityShareholderForLegalRep> {
    const response = await api.get(
      '/majority-shareholder/for-legal-representative',
      creditApplicationId ? { params: { creditApplicationId } } : undefined
    )

    if (response.data.success && response.data.data) {
      return response.data.data
    }

    return response.data
  }

  async getShareholderCalendlyInfo(): Promise<UserInfo> {
    const response = await api.get('/majority-shareholder/info')
    return response.data.data
  }

  async setAsLegalRepresentative(
    shareholderId: string
  ): Promise<MajorityShareholder> {
    const response = await api.patch(
      `/majority-shareholder/${shareholderId}/set-as-legal-representative`
    )

    if (response.data.success && response.data.data) {
      return response.data.data
    }

    return response.data
  }

  async createLegalRepresentative(
    formData: any,
    files: { ineFront: File | null; ineBack: File | null },
    creditApplicationId?: string | null
  ): Promise<MajorityShareholder> {
    const formDataToSend = new FormData()

    // Agregar campos del formulario
    formDataToSend.append('firstName', formData.nombre)
    formDataToSend.append('paternalLastName', formData.apellidoPaterno)
    formDataToSend.append('maternalLastName', formData.apellidoMaterno)
    formDataToSend.append('rfc', formData.rfc.toUpperCase())
    formDataToSend.append('birthDate', '2000-01-01') // Por defecto, ya que no lo pedimos en el form
    formDataToSend.append('nationality', 'Mexicana')
    formDataToSend.append('phoneNumber', formData.numeroCelular)
    formDataToSend.append('email', formData.correoElectronico.toLowerCase())

    // Dirección dummy (no la pedimos en el form de legal representative)
    formDataToSend.append('street', 'N/A')
    formDataToSend.append('exteriorNumber', 'N/A')
    formDataToSend.append('betweenStreets', '')
    formDataToSend.append('postalCode', '00000')
    formDataToSend.append('neighborhood', 'N/A')
    formDataToSend.append('state', 'N/A')
    formDataToSend.append('country', 'México')

    // Agregar archivos INE
    if (files.ineFront) {
      formDataToSend.append('ineFrontImage', files.ineFront)
    }
    if (files.ineBack) {
      formDataToSend.append('ineBackImage', files.ineBack)
    }

    if (creditApplicationId) {
      formDataToSend.append('creditApplicationId', creditApplicationId)
    }

    const response = await api.post(
      '/majority-shareholder/legal-representative',
      formDataToSend,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )

    console.log('Create legal representative response:', response.data)

    if (response.data.success && response.data.data) {
      return response.data.data
    }

    return response.data
  }

  // Helper function to transform frontend form data to backend DTO
  transformFormDataToDto(formData: any): CreateMajorityShareholderCompleteDto {
    // Asegurarse de que neighborhood sea un string
    let neighborhoodValue = formData.colony || formData.neighborhood || ''

    // Si es un objeto, intentar obtener el valor o label
    if (typeof neighborhoodValue === 'object' && neighborhoodValue !== null) {
      neighborhoodValue =
        neighborhoodValue.value || neighborhoodValue.label || ''
    }

    // Si es la cadena "[object Object]", limpiarla
    if (neighborhoodValue === '[object Object]') {
      neighborhoodValue = ''
    }

    return {
      firstName: formData.firstName,
      paternalLastName: formData.lastName || formData.paternalLastName,
      maternalLastName: formData.motherLastName || formData.maternalLastName,
      rfc: formData.rfc.toUpperCase(),
      birthDate: formData.birthDate,
      nationality:
        formData.nationality === 'mexico' ? 'Mexicana' : formData.nationality,
      phoneNumber: formData.phoneNumber,
      email: formData.email,
      street: formData.street,
      exteriorNumber: formData.exteriorNumber,
      interiorNumber: formData.interiorNumber || undefined,
      betweenStreets: formData.betweenStreets || '',
      postalCode: formData.postalCode,
      neighborhood: neighborhoodValue,
      city: formData.city,
      municipality: formData.municipality || undefined,
      state: formData.state,
      stateAbbreviation: formData.stateAbbreviation || undefined,
      country: 'México',
    }
  }
}

export default new MajorityShareholderService()
